// src/lib/financeiro/calcularPontosMensais.ts
import type { FaturaPainel, PontoMensal } from './types.ts';
import { valorDevido } from '../statusBoleto.ts';
import { ehCobranca, estaPago, faturasDoPainel } from './definicoesKpi.ts';
import { mesDaBase, SEM_DATA } from './baseDataFatura.ts';
import type { BaseDataFinanceiro } from './baseDataFatura.ts';

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

// "JAN/2026" -> "2026-01", só para poder comparar como string e ordenar
// cronologicamente. O balde sem data vai para o FIM: o mini-card mostra o
// último ponto da série, e "Sem data" ali não diria nada a ninguém.
function mesOrdenavel(mes: string): string {
  const [nome, ano] = mes.split('/');
  const idx = MESES.indexOf(nome);
  if (idx === -1 || !ano) return '9999-99';
  return `${ano}-${String(idx + 1).padStart(2, '0')}`;
}

/**
 * Agrega faturas já filtradas em pontos mensais (Emitido/Pago/Em aberto/Vencido
 * + taxa de inadimplência do mês), ordenados cronologicamente. Não aplica nenhum
 * corte de quantidade de meses — isso é responsabilidade de quem chama.
 *
 * @param base em qual data a fatura é pendurada. ⚠️ Tem de ser a MESMA base que
 *   `filtrarFaturasPorPeriodo` usou. Com o filtro por vencimento e a série por
 *   referência, o último ponto podia cair fora do período escolhido: em
 *   setembro/2026 o mini-card dizia "emitido em NOV/2026", por causa de duas
 *   parcelas de renegociação (referência OUT e NOV) que vencem em setembro.
 */
export function calcularPontosMensais(
  dados: FaturaPainel[],
  base: BaseDataFinanceiro = 'referencia',
): PontoMensal[] {
  const acc = new Map<string, PontoMensal>();
  // Conta interna fora do gráfico também, pelo mesmo motivo dos KPIs (5955).
  faturasDoPainel(dados).forEach((f) => {
    // Mesmo critério e mesmo valor dos KPIs (definicoesKpi.ts): fatura sem
    // cobrança não entra, e boleto único vale `valor_emissao_boleto_unico`.
    if (!ehCobranca(f)) return;
    const mes = mesDaBase(f, base) || SEM_DATA;
    if (!acc.has(mes)) acc.set(mes, { mes, emitido: 0, pago: 0, emAberto: 0, vencido: 0, taxaInadimplencia: 0 });
    const p = acc.get(mes)!;
    const valor = valorDevido(f);
    p.emitido += valor;
    if (estaPago(f)) p.pago += valor;
    if (f.statusDerivado === 'Em aberto') p.emAberto += valor;
    if (f.statusDerivado === 'Vencido') p.vencido += valor;
  });
  const lista = Array.from(acc.values()).map((p) => ({ ...p, taxaInadimplencia: p.emitido > 0 ? p.vencido / p.emitido : 0 }));
  lista.sort((a, b) => (mesOrdenavel(a.mes) > mesOrdenavel(b.mes) ? 1 : -1));
  return lista;
}
